<template>
    <app-layout title="Checkout">
        <div class="lg:flex">
            <sidebar-catalogue :categories="categories" class="z-40 relative" />

            <div class="checkout flex-1 relative z-30">
                <div class="p-4 xl:p-8" v-if="!isFetching">
                    <h1 class="text-xl lg:text-3xl">Checkout - {{ activeCustomer.name }}</h1>

                    <ul v-if="cartChanges && cartChanges.length" class="my-4">
                        <li v-for="(change, index) in cartChanges" :key="index" class="bg-blue-100 border border-blue-300 p-2 my-1">
                            {{ change.message}}

                            <ul v-if="change.products && change.products.length" class="list-disc ml-10">
                                <li v-for="(product, index) in change.products" :key="'product' + index">{{ product }}</li>
                            </ul>
                        </li>
                    </ul>

                    <shopping-cart :isTaxExemptCustomer="isTaxExemptCustomer" :delivery_fee="deliveryFee" :appliableCreditAmount="appliableCreditAmount"/>

                    <div v-show="isSchoolCustomer" class="pt-1 sm:pt-2 pb-0 lg:px-8 sm:font-semibold text-gray-500 text-xs xl:text-sm text-center">
                        <div class="sm:hidden">Order deadline is Wed. @ 11:59pm for delivery the next week</div>
                        <div class="hidden sm:block sm:text-sm">Order deadline is Wednesday at 11:59pm for delivery the following week</div>
                    </div>

                    <form @submit.prevent="handleSubmit" class="mt-16">
                        <div class="flex gap-x-8">
                            <div class="form-group">
                                <jet-label value="Shipping Method" />
                                <div class="flex w-40 sm:w-52">
                                    <multiselect class="multi-lg h-7 text-base"
                                        v-model="form.shipping_method"
                                        :options="shippingOptions"
                                        valueProp="value"
                                        trackBy="value"
                                        label="label"
                                        :canClear="false"
                                        :canDeselect="false"
                                        @input="fetchOpenOrder"
                                    />
                                    <jet-input-error :message="$page.props.errors.shipping_method" class="m1" />
                                </div>
                            </div>

                            <div class="form-group">
                                <jet-label value="Ship Date" />
                                <div class="flex gap-x-6">
                                    <datepicker class=" w-40 sm:w-52" inputClassName="dp-custom-input"
                                        v-model="form.ship_date"
                                        weekStart="0"
                                        monthNameFormat="long"
                                        :enableTimePicker="false"
                                        :autoApply="true"
                                        :is24="false"
                                        :clearable="false"
                                        :minDate="minDate"
                                        :maxDate="maxDate"
                                        :disabled-dates="invalidDeliveryDates"
                                        :format="formatDate"
                                        @update:modelValue="fetchOpenOrder()"
                                    />
                                    <div v-show="openOrder">
                                        <div v-show="isSchoolCustomer" class="flex items-center">
                                            <div class="px-2 leading-tight" style="margin-top: -3px;">
                                                <div class="font-semibold">This will be added to your order #{{ form.add_to_order_id }}</div>
                                                <div class="text-xs">
                                                    Ordered by {{ openOrder ? openOrder.purchaser.name : '' }} on {{ openOrder ? formatOrderDate(openOrder.order_date) : '' }}
                                                </div>
                                            </div>
                                        </div>
                                        <div v-show="!isSchoolCustomer" class="flex items-center">
                                            <input type="checkbox" class="rounded h-6 w-6" name="add_to_order" v-model="form.add_to_order"/>
                                            <div class="px-2 leading-tight" style="margin-top: -3px;">
                                                <div class="font-semibold">Add to your order #{{ form.add_to_order_id }}</div>
                                                <div class="text-xs">
                                                    Ordered by {{ openOrder ? openOrder.purchaser.name : '' }} on {{ openOrder ? formatOrderDate(openOrder.order_date) : '' }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <jet-input-error :message="form.errors.ship_date" />
                                <div v-if="cartHasSpecialOrderItems && specialOrderDeliveryDate" class="ml-3 leading-loose">
                                    *Special order items will be {{ form.shipping_method === 'pickup' ? 'ready for pickup' : 'delivered' }}
                                    on {{ specialOrderDeliveryDate }}
                                </div>
                                <jet-input-error v-if="form.errors.ship_date !== form.errors.special_order_ship_date" :message="form.errors.special_order_ship_date" />
                                <div v-if="cartHasDairyItems && specialOrderDeliveryDate" class="ml-3 leading-loose">
                                    *If ordered after 10am or on weekends & holidays for {{ form.shipping_method }} before {{ specialOrderDeliveryDate }} all dairy items will be removed from your cart to fulfill your order in time.
                                </div>
                            </div>
                        </div>

                        <div class="mb-8">
                            <div class="flex items-center">
                                <input type="checkbox" class="rounded h-6 w-6" name="allow_substitutions" v-model="form.allow_substitutions"/>
                                <div class="px-2 leading-tight" style="margin-top: -3px;">
                                    <div class="font-semibold">Allow Substitutions</div>
                                </div>
                            </div>
                            <div class="text-xs pl-8">
                                If an item is unavailable when preparing your order,<br/>allow us to substitute some or all of it for a similar item.
                            </div>
                        </div>
                        
                        <div class="form-group">
                            <jet-label value="Order Notes / Shipping Instructions" />
                            <textarea v-model="form.notes" class="rounded w-full max-w-2xl h-28"></textarea>
                            <jet-input-error :message="form.errors.notes" />
                        </div>

                        <jet-button type="submit" :disabled="form.processing || !canPlaceOrder">Place Order</jet-button>

                        <span v-if="!canPlaceOrder" class="ml-4 text-red-600">
                            Order minimum of {{ toCurrency(parseFloat(orderMinimum)) }} not met.
                        </span>
                        <span v-else-if="!minimumMet" class="ml-4 text-gray-500">
                            A delivery fee of {{ toCurrency(parseFloat(deliveryFeeBelowMinimum)) }} applies to deliveries below {{ toCurrency(parseFloat(orderMinimum)) }}.
                        </span>
                    </form>
                </div>

                <div v-else class="loading">
                    <div class="text-center text-lg font-bold flex flex-col items-center justify-center min-h-screen">
                        <img src="/images/icons/loading-pulse.svg" height="128" width="128" class="" />
                        <p>Loading ...</p>
                    </div>
                </div>
            </div>
        </div>

    </app-layout>
</template>

<script>
import { onMounted } from 'vue';
import { useForm, usePage } from '@inertiajs/inertia-vue3'
import AppLayout from '@/Layouts/AppLayout'
import SidebarCatalogue from '@/Layouts/SidebarCatalogue';
import ShoppingCart from '@/Pages/Checkout/Partials/ShoppingCart';
import JetButton from '@/Jetstream/Button';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import UseCart from '@/Composables/UseCart';
import Datepicker from '@vuepic/vue-datepicker';
import { DateTime } from "luxon";
import Multiselect from '@vueform/multiselect';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';


export default {
    components: {
        AppLayout,
        Datepicker,
        SidebarCatalogue,
        ShoppingCart,
        JetButton,
        JetLabel,
        JetInput,
        JetInputError,
        Multiselect,
    },

    props: ['categories', 'holidays', 'cartChanges', 'orderMinimum', 'deliveryFeeBelowMinimum'],

    setup (props) {
        const form = useForm({
            shipping_method: usePage().props.value.activeCustomer.default_shipping_method,
            delivery_fee: usePage().props.value.activeCustomer.delivery_fee,
            allow_substitutions: false,
            ship_date: null,
            special_order_ship_date: null,
            add_to_order: false,
            add_to_order_id: null,
            notes: usePage().props.value.activeCustomer.order_notes,
        });

        const { fetchCart, isFetching, cartTotal, cartHasSpecialOrderItems, cartCategories } =
            UseCart(
                usePage().props.value.user,
                usePage().props.value.activeCustomer ? usePage().props.value.activeCustomer.tax_exempt : 0
            );

        onMounted(fetchCart);

        return { form, fetchCart, isFetching, cartTotal, cartHasSpecialOrderItems, cartCategories };
    },

    data() {
        return {
            shippingOptions: [
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
            ],

            openOrder: null,
        }
    },

    methods: {

        fetchOpenOrder(shipping_method = this.form.shipping_method, date = this.form.ship_date, ) {

            axios
                .get(route('customer_orders.fetch', {
                    date: DateTime.fromJSDate(date).toFormat('yyyy-LL-dd'),
                    shipping_method: shipping_method
                }))
                .then(response => {
                    this.openOrder = response.data && response.data !== null ? response.data : null;
                    this.form.add_to_order = response.data ? true : false;
                    this.form.add_to_order_id = response.data ? response.data.id : null;
                })
                .catch(error => {
                    console.log(error);
                });
        },

        handleSubmit () {
            if (!this.form.processing) {
                this.form.transform(data => {
                    const ship_date = data.ship_date ?
                        DateTime.fromJSDate(data.ship_date).toFormat('yyyy-LL-dd') :
                        null;

                    return {
                        ...data,
                        ship_date,
                    }
                })
                .post(this.route('checkout.store'), {
                    onSuccess: () => {
                        this.fetchCart();
                        this.setDefaultDeliveryDate();
                    },
                });
            }
        },

        setDefaultDeliveryDate() {
            let customerDays = this.activeCustomer.delivery_days.toLowerCase();
            let nextDeliveryDay = DateTime.now();
            let nextWeek = nextDeliveryDay.plus({ weeks:2 });
            let today = DateTime.now();

            while (nextDeliveryDay <= nextWeek) {
                // Skip today if we're passed the 12:05am cutoff
                if (nextDeliveryDay.hasSame(today, 'day') && ((today.hour === 0 && today.minute >= 5) || today.hour > 0)) {
                    nextDeliveryDay = nextDeliveryDay.plus({ days: 1 });
                    continue;
                }

                let isBeforeEarliestShipDay = false;
                if (this.activeCustomer.type === 'School' && nextDeliveryDay.diff(this.earliestPossibleShipDateForSchool, 'days').days < 0) {
                    isBeforeEarliestShipDay = true;
                }

                let isHoliday = this.holidays.includes(nextDeliveryDay.toFormat('yyyy-LL-dd'));

                if (!isHoliday
                    && !isBeforeEarliestShipDay
                    && ((this.activeCustomer.type !== 'School'
                            && customerDays.includes(nextDeliveryDay.setLocale("en").weekdayLong.toLowerCase()))
                        || (this.activeCustomer.type === 'School'
                            && !this.invalidDeliveryDatesYMD.includes(nextDeliveryDay.toFormat('yyyy-LL-dd'))))
                ) {
                    this.form.ship_date = nextDeliveryDay.toJSDate();
                    this.fetchOpenOrder(this.form.shipping_method, nextDeliveryDay.toJSDate());

                    return;
                }

                nextDeliveryDay = nextDeliveryDay.plus({ days: 1 });
            }
        },

        formatDate(date) {
            return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
        },

        formatOrderDate(date) {
            return DateTime.fromISO(date).toFormat('MMM d, yyyy');
        }
    },

    computed: {
        minDate () {
            let today = DateTime.now();

            // If delivery and past the 12:05am cutoff, set for tomorrow
            if (this.form.shipping_method === 'delivery') {
                if (today.hour == 0 && today.minute < 5) {
                    return today.toJSDate();
                } else {
                    return today.plus({ days: 1 }).toJSDate();
                }
            }
            else if (today.hour >= 16) {
                return today.plus({ days: 1 }).toJSDate();
            }

            return today.toJSDate();
        },

        maxDate () {
            return DateTime.now().plus({ months: 3 }).toJSDate();
        },

        invalidDeliveryDates() {
            if (!this.activeCustomer) {
                return [];
            }

            let invalidDates = [];
            let customerDays = this.activeCustomer.delivery_days.toLowerCase();
            let nextDate = DateTime.fromJSDate(this.minDate);
            let endDate = DateTime.fromJSDate(this.maxDate);
            let isDelivery = this.form.shipping_method == 'delivery';
            let holiday_adjusted_ship_dates = [];
            let holiday_adjusted_ship_date_def = [];
            let non_adjustable_holidays = [];

            // Get Holiday Adjustments for Schools
            if (this.activeCustomer.type === 'School') {
                let school_holidays = this.holidays;
                let school_only_holidays = ['2025-04-21'];
                school_holidays = school_holidays.concat(school_only_holidays);

                let holidays_desc = school_holidays.sort((a, b) => new Date(b) - new Date(a));

                // Adjust for holidays
                holidays_desc.forEach((holiday) => {
                    let holidate = DateTime.fromISO(holiday);
                    let day_of_week = holidate.setLocale("en").weekdayLong.toLowerCase()
                    let isMonday = day_of_week === 'monday';
                    let tmrw_date = holidate.plus({ days: 1 });

                    // Monday => Tuesday and Tuesday => Wednesday
                    if (isMonday && !holiday_adjusted_ship_date_def.hasOwnProperty(tmrw_date.toFormat('yyyy-LL-dd')))
                    {
                        let tmrw_tmrw_date = tmrw_date.plus({ days: 1 });
                        holiday_adjusted_ship_date_def[tmrw_date.toFormat('yyyy-LL-dd')] = {
                            holiday: false,
                            adj_date: tmrw_tmrw_date.toFormat('yyyy-LL-dd'),
                        };
                    }

                    holiday_adjusted_ship_date_def[holidate.toFormat('yyyy-LL-dd')] = {
                        holiday: true,
                        adj_date: tmrw_date.toFormat('yyyy-LL-dd'),
                    };
                });

                // Adjust for adjusted dates falling on holidays
                for (let [holidate, holidate_data] of Object.entries(holiday_adjusted_ship_date_def))
                {
                    let new_date = holidate_data['adj_date'];
                    while (holiday_adjusted_ship_date_def.hasOwnProperty(new_date) && holiday_adjusted_ship_date_def[new_date]['holiday'])
                    {
                        new_date = holiday_adjusted_ship_date_def[new_date]['adj_date'];
                        holiday_adjusted_ship_date_def[holidate]['adj_date'] = new_date;
                    }
                };
            }

            while (nextDate <= endDate) {

                let isBeforeEarliestShipDay = false;
                if (this.activeCustomer.type === 'School' && nextDate.diff(this.earliestPossibleShipDateForSchool, 'days').days < 0) {
                    isBeforeEarliestShipDay = true;
                }

                let checkDay = nextDate.setLocale("en").weekdayLong.toLowerCase();
                let isSunday = checkDay === 'sunday';
                let deliveryNotAccepted = !customerDays.includes(checkDay);
                let isHoliday = this.holidays.includes(nextDate.toFormat('yyyy-LL-dd'));
                let isAdjustedDueToHoliday = holiday_adjusted_ship_date_def.hasOwnProperty(nextDate.toFormat('yyyy-LL-dd'));
                let isHolidayAdjustedDate = holiday_adjusted_ship_dates.includes(nextDate.toFormat('yyyy-LL-dd'))

                if ((isSunday || isHoliday || (isDelivery && deliveryNotAccepted) || isBeforeEarliestShipDay || isAdjustedDueToHoliday) && !isHolidayAdjustedDate) {
                    invalidDates.push(nextDate.toJSDate());

                    if (customerDays.includes(checkDay) && isAdjustedDueToHoliday && !non_adjustable_holidays.includes(nextDate.toFormat('yyyy-LL-dd')))
                    {
                        holiday_adjusted_ship_dates.push(holiday_adjusted_ship_date_def[nextDate.toFormat('yyyy-LL-dd')]['adj_date']);
                    }
                }

                nextDate = nextDate.plus({ days: 1 });
            }

            return invalidDates;
        },

        invalidDeliveryDatesYMD () {
            let invalidDeliveryDatesYMD = [];

            this.invalidDeliveryDates.forEach(date => {
                invalidDeliveryDatesYMD.push(DateTime.fromJSDate(date).toFormat('yyyy-LL-dd'));
            });

            return invalidDeliveryDatesYMD;
        },

        specialOrderDeliveryDate() {
            if((this.cartHasSpecialOrderItems || this.cartHasDairyItems) && this.form.ship_date != null) {
                let today = DateTime.now();
                let nextAvailableDay;

                // If Sunday, go to Monday
                if (today.weekday === 5) nextAvailableDay = today.plus({ days: 4 });
                else if (today.weekday === 6) nextAvailableDay = today.plus({ days: 3 });
                else nextAvailableDay = today.plus({ days: 2 })

                // If the selected delivery day is on or after the next available day, exit
                let shipDate = DateTime.fromJSDate(this.form.ship_date);

                if(shipDate.startOf('day') >= nextAvailableDay.startOf('day')) {
                    this.form.special_order_ship_date = shipDate.toFormat('yyyy-LL-dd');

                    return false;
                }
                else {
                    // Otherwise, find the customer's next acceptable delivery day
                    // make sure it doesn't fall on a holiday.
                    let customerDays = this.activeCustomer.delivery_days.toLowerCase();

                    let found = false;
                    while (!found) {
                        let isHoliday = this.holidays.includes(nextAvailableDay.toFormat('yyyy-LL-dd'));

                        if (!isHoliday
                            && (    (this.activeCustomer.type !== 'School'
                                    && customerDays.includes(nextAvailableDay.setLocale("en").weekdayLong.toLowerCase()))
                                || (this.activeCustomer.type === 'School'
                                    && !this.invalidDeliveryDatesYMD.includes(nextAvailableDay.toFormat('yyyy-LL-dd'))))) {
                            found = true;
                        } else {
                           nextAvailableDay = nextAvailableDay.plus({ days: 1 });
                        }
                    }
                    this.form.special_order_ship_date = nextAvailableDay.toFormat('yyyy-LL-dd');

                    return nextAvailableDay.toLocaleString({ month: 'long', day: 'numeric'});
                }

            }

            return false;
        },

        activeCustomer() {
            return this.$page.props.activeCustomer;
        },

        isSchoolCustomer() {
            return this.activeCustomer?.type === 'School';
        },

        isTaxExemptCustomer() {
            return this.activeCustomer?.tax_exempt;
        },

        dairyCategory() {
            let category = this.categories.find(c => c.id == 96);

            if(category === undefined) return null;

            return category.id;
        },

        cartHasDairyItems() {
            if(this.dairyCategory === null) return false;

            return this.cartCategories.has(this.dairyCategory);
        },

        canPlaceOrder() {
            // Pickup
            if(this.form.shipping_method === 'pickup') return true;
            // Delivery
            else if (this.cartTotal >= this.orderMinimum) return true;
            else if (this.deliveryFeeBelowMinimum !== null) return true;
            else return false;
        },

        minimumMet() {
            if(this.form.shipping_method === 'pickup') return true;
            return this.form.shipping_method === 'delivery' && this.cartTotal >= this.orderMinimum;
        },

        deliveryFee() {
            if (this.form.shipping_method === 'pickup')
                return null;
            else if (this.deliveryFeeBelowMinimum === null || this.minimumMet)
                return this.form.delivery_fee;
            else
                return this.deliveryFeeBelowMinimum;
        },

        appliableCreditAmount () {
            // if (this.activeCustomer && this.activeCustomer.auto_apply_credit && this.activeCustomer.credit_balance !== null)
            // {
            //     return this.roundDecimals(parseFloat(this.activeCustomer.credit_balance));
            // }
            return 0;
        },

        earliestPossibleShipDateForSchool() {
            let orderDate = DateTime.now();
            let orderDayOfWeek = orderDate.toFormat('E');

            if (orderDayOfWeek > 3) { // Order day is after Wednesday
                return orderDate.plus({weeks:2}).startOf('week');
            }
            return orderDate.plus({weeks:1}).startOf('week');
        }

    },

    watch: {
        activeCustomer: {
            handler(newVal, oldVal) {
                if(newVal.id !== oldVal.id) {
                    this.fetchCart();
                    this.form.ship_date = null;
                    this.form.notes = newVal.order_notes;
                    this.setDefaultDeliveryDate();
                }
            },
            deep: true
        },
        'form.shipping_method' : {
            handler(newVal, oldVal) {
                if(oldVal === 'pickup' && newVal === 'delivery') {
                    let shipDate = DateTime.fromJSDate(this.form.ship_date).toFormat('yyyy-LL-dd');

                    let invalidDate = this.invalidDeliveryDates.find(i => {
                        return shipDate === DateTime.fromJSDate(i).toFormat('yyyy-LL-dd');
                    });

                    if(invalidDate !== undefined || (DateTime.fromJSDate(this.form.ship_date) < DateTime.fromJSDate(this.minDate))) {
                        this.form.ship_date = null;
                    }
                }
            }
        }
    },

    mounted () {
        this.setDefaultDeliveryDate();
    },
}
</script>

<style>
.dp-custom-input {
    @apply h-8;
}
</style>

<style scoped>
.loading {
    @apply absolute top-0 left-0 right-0 bottom-0 bg-white bg-opacity-75;
}

@media (min-width: 1024px) {
    .checkout {
        padding-left: 240px;
    }

    .loading {
        left: 240px;
    }
}
</style>
